import type { LabelOperator, Relationship } from '@/types';

import { ALL_RELATIONSHIP_DEFINITIONS } from './definitions';
import type { RelationshipDefinition, RelationshipLabel } from './types';

/**
 * Get all allowed relationship labels between two entity types
 *
 * This is the main function EdgeLabel will call to populate the dropdown.
 * Returns only relationships that are valid for the given source/target pair.
 *
 * @param sourceLabel - Source node's firstLabel (e.g., "Asset", "ADObject", "Port")
 * @param targetLabel - Target node's firstLabel (e.g., "Port", "ADObject", "Risk")
 * @returns Array of valid RelationshipLabel objects for the dropdown
 *
 * @example
 * getAllowedRelationships("Asset", "Port")
 * // Returns: [{ actual: "HAS_PORT", display: "Has Port" }]
 *
 * @example
 * getAllowedRelationships("Asset", "Asset")
 * // Returns: [{ actual: "DISCOVERED", display: "Discovered" }]
 *
 * @example
 * getAllowedRelationships("ADObject", "ADObject")
 * // Returns: [
 * //   { actual: "MemberOf", display: "Member Of" },
 * //   { actual: "AdminTo", display: "Admin To" },
 * //   ... (all 85 AD relationships)
 * // ]
 */
export function getAllowedRelationships(
  sourceLabel: string,
  targetLabel: string
): RelationshipLabel[] {
  const allowed: RelationshipLabel[] = [];

  for (const definition of ALL_RELATIONSHIP_DEFINITIONS) {
    if (isAllowedBetween(definition, sourceLabel, targetLabel)) {
      // Add ALL labels from this definition (aliasing support)
      allowed.push(...definition.labels);
    }
  }

  // Sort alphabetically by display name
  return allowed.sort((a, b) => a.display.localeCompare(b.display));
}

/**
 * Check if a relationship definition allows a specific source/target pair
 * Respects bidirectional flag for reverse lookups
 *
 * @param definition - Relationship definition to check
 * @param sourceLabel - Source entity label
 * @param targetLabel - Target entity label
 * @returns true if the source/target pair is allowed by this definition
 */
function isAllowedBetween(
  definition: RelationshipDefinition,
  sourceLabel: string,
  targetLabel: string
): boolean {
  for (const rule of definition.allowedBetween) {
    // Check forward direction (source → target)
    if (rule.source === sourceLabel && rule.target === targetLabel) {
      return true;
    }

    // Check reverse direction if bidirectional (target → source)
    if (
      definition.bidirectional &&
      rule.source === targetLabel &&
      rule.target === sourceLabel
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Normalize RelationshipLabel.actual to an array of strings
 */
export function normalizeActual(label: RelationshipLabel): string[] {
  return Array.isArray(label.actual) ? label.actual : [label.actual];
}

/**
 * Derive edge data properties from a RelationshipLabel
 *
 * This is the bridge between the RelationshipLabel (source of truth for edge behavior)
 * and FlatGraphEdge.data. When a user selects a label, this function produces the
 * corresponding edge data values.
 *
 * @param label - The selected RelationshipLabel
 * @returns Object with label(s), label_operator, length, optional, and not_exists
 */
export function getEdgeDataFromLabel(label: RelationshipLabel): {
  label: Relationship | Relationship[];
  label_operator: LabelOperator;
  length: number | undefined;
  optional: boolean;
  not_exists: boolean;
} {
  const actuals = normalizeActual(label) as Relationship[];
  const isMulti = actuals.length > 1;

  return {
    label: isMulti ? actuals : actuals[0],
    label_operator: isMulti ? 'OR' : '',
    length: label.length,
    optional: label.optional ?? false,
    not_exists: label.notExists ?? false,
  };
}

/**
 * Check if a specific relationship label is valid between two entity types
 * Useful for validation when a user selects a relationship
 *
 * @param sourceLabel - Source entity label
 * @param targetLabel - Target entity label
 * @param relationshipActual - The actual relationship type to validate
 * @returns true if the relationship is valid for this source/target pair
 *
 * @example
 * isValidRelationship("Asset", "Port", "HAS_PORT") // true
 * isValidRelationship("Port", "Asset", "HAS_PORT") // false (not bidirectional)
 * isValidRelationship("ADObject", "ADObject", "MemberOf") // true
 */
/**
 * Find the RelationshipDefinition that contains a label matching the given actual value.
 *
 * @param actualLabel - A Neo4j relationship type string (e.g., "IAM_AWS_PERMISSION", "MemberOf")
 * @returns The matching RelationshipDefinition, or undefined if not found
 */
export function getRelationshipDefinition(
  actualLabel: string
): RelationshipDefinition | undefined {
  return ALL_RELATIONSHIP_DEFINITIONS.find(def =>
    def.labels.some(label => normalizeActual(label).includes(actualLabel))
  );
}

export function isValidRelationship(
  sourceLabel: string,
  targetLabel: string,
  relationshipActual: string
): boolean {
  const allowed = getAllowedRelationships(sourceLabel, targetLabel);
  return allowed.some(label =>
    normalizeActual(label).includes(relationshipActual)
  );
}
